import {
	Box,
	Button,
	Chip,
	CircularProgress,
	MenuItem,
	Paper,
	TextField,
	Typography,
	makeStyles,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import DatePickerACCB from "./DatePickerACCB";
import RoomSlotPicker, { formatTime12 } from "./RoomSlotPicker";
import { safeDateFormat } from "../utils";
import {
	getAdjournments,
	createAdjournment,
	actionAdjournment,
	searchAvailableRooms,
} from "../service";
import {
	PRIVILEGED_TYPES,
	buildPartyLabels,
	formatApiDate,
	nextHearingText,
	requesterInfo,
	statusStyle,
} from "./adjournmentUtils";

const useStyles = makeStyles(() => ({
	card: {
		width: "100%",
		// Keep the padding + border inside the 100% width; without this the card
		// overflows its parent by 4rem + 2px (no global border-box reset here).
		boxSizing: "border-box",
		padding: "2rem",
		border: "1px solid #E5E5E5",
		maxHeight: "90vh",
		overflowY: "auto",
	},
}));

const REASON_PREVIEW_LENGTH = 300;

const previewReason = (reason) => {
	const text = reason || "";
	return text.length > REASON_PREVIEW_LENGTH
		? `${text.slice(0, REASON_PREVIEW_LENGTH)}...`
		: text;
};

// One "label — value" line of an adjournment record.
function RecordField({ label, value }) {
	if (!value) return null;
	return (
		<Box display="flex" style={{ gap: "0.5rem", marginTop: "0.25rem" }}>
			<Typography
				style={{ fontSize: "0.8rem", color: TEXT.muted, minWidth: "7.5rem", flexShrink: 0 }}
			>
				{label}
			</Typography>
			<Typography
				style={{
					fontSize: "0.85rem",
					color: TEXT.secondary,
					minWidth: 0,
					overflowWrap: "anywhere",
					wordBreak: "break-word",
				}}
			>
				{value}
			</Typography>
		</Box>
	);
}

// Short "Room X · 4:00 PM – 4:30 PM, 4:30 PM – 5:00 PM" label for the chosen
// slots (all from the same room — RoomSlotPicker enforces that).
const slotSummary = (slots) => {
	if (!slots || slots.length === 0) return "";
	const first = slots[0];
	const room = first.room_name || (first.room_id ? `Room ${first.room_id}` : "");
	const times = [...slots]
		.sort((a, b) => String(a.from_time).localeCompare(String(b.from_time)))
		.map((slot) => `${formatTime12(slot.from_time)} – ${formatTime12(slot.to_time)}`)
		.join(", ");
	return room ? `${room} · ${times}` : times;
};

export default function Adjournments({
	disputeId,
	profile,
	allProfiles,
	onOpenPrevious,
	isReschedule = false,
}) {
	const classes = useStyles();
	const isPrivileged = PRIVILEGED_TYPES.includes(profile?.profile_type);
	const partyLabels = buildPartyLabels(allProfiles);

	// Same component drives both the Adjournment and the Reschedule module — only
	// the wording and the is_adjourn flag differ.
	const moduleTitle = isReschedule ? "Reschedule" : "Adjournments";
	const submitLabel = isReschedule ? "Reschedule" : "Adjourn";

	const [items, setItems] = useState([]);
	const [hearings, setHearings] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [notice, setNotice] = useState("");

	// submit form
	const [reason, setReason] = useState("");
	const [proposedDate, setProposedDate] = useState(null);
	const [hearingId, setHearingId] = useState("");
	const [submitting, setSubmitting] = useState(false);

	// Room/slot re-pick for a DIRECT (privileged) adjournment. The hearing is
	// re-booked onto a free slot on the new date — never a silent reuse.
	const [rooms, setRooms] = useState([]);
	const [roomsLoading, setRoomsLoading] = useState(false);
	const [roomsError, setRoomsError] = useState("");
	const [selectedSlots, setSelectedSlots] = useState([]);

	// per-pending-request inputs (privileged view)
	const [rejectNotes, setRejectNotes] = useState({});
	const [approveDates, setApproveDates] = useState({});
	const [actioningId, setActioningId] = useState(null);
	// Room/slot re-pick per pending request (keyed by adj.id) for approvals.
	const [approveRooms, setApproveRooms] = useState({});   // id -> rooms_data[]
	const [approveSlots, setApproveSlots] = useState({});   // id -> selected slots[]
	const [approveRoomsKey, setApproveRoomsKey] = useState({}); // id -> "id:date" loaded

	const fetchItems = () => {
		setLoading(true);
		setError("");
		getAdjournments(disputeId, !isReschedule)
			.then((res) => {
				const data = res.data || {};
				setItems(Array.isArray(data.adjournments) ? data.adjournments : []);
				setHearings(Array.isArray(data.hearings) ? data.hearings : []);
			})
			.catch(() => setError("Failed to load requests."))
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		fetchItems();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [disputeId, isReschedule]);

	const selectedHearing = hearings.find((h) => String(h.id) === String(hearingId));

	// Auto-load available rooms for the DIRECT adjournment form whenever the
	// privileged user has picked a new date (and, ideally, a hearing so we know
	// its stage for recommended-duration highlighting).
	useEffect(() => {
		if (!isPrivileged) {
			setRooms([]);
			setSelectedSlots([]);
			return;
		}
		const dateStr = formatApiDate(proposedDate);
		setSelectedSlots([]);
		if (!dateStr) {
			setRooms([]);
			return;
		}
		const stageId = selectedHearing?.stage_id || null;
		setRoomsLoading(true);
		setRoomsError("");
		searchAvailableRooms(dateStr, stageId)
			.then((res) => setRooms(res.data?.rooms_data || []))
			.catch(() => setRoomsError("Failed to load hearing rooms for this date."))
			.finally(() => setRoomsLoading(false));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [proposedDate, hearingId, isPrivileged]);

	// Rescheduling is always done directly by the admin, so the Reschedule
	// module has no party requests to review — only Adjournments does.
	const showPendingRequests = !isReschedule;
	const pendingItems = showPendingRequests
		? items.filter((adj) => adj.status === "pending")
		: [];

	// Effective approval date for a pending request: the approver's override,
	// else the party's proposed date.
	const approvalDateValue = (adj) =>
		approveDates[adj.id] !== undefined
			? approveDates[adj.id]
			: adj.proposed_date
				? new Date(adj.proposed_date)
				: null;

	// Load available rooms for a pending request's approval slot picker.
	const loadApproveRooms = (adj, dateStr, key) => {
		setApproveRoomsKey((prev) => ({ ...prev, [adj.id]: key }));
		setApproveSlots((prev) => ({ ...prev, [adj.id]: [] }));
		searchAvailableRooms(dateStr, adj.hearing_stage_id || null)
			.then((res) =>
				setApproveRooms((prev) => ({ ...prev, [adj.id]: res.data?.rooms_data || [] }))
			)
			.catch(() =>
				setApproveRooms((prev) => ({ ...prev, [adj.id]: [] }))
			);
	};

	// Auto-load approval rooms for each pending request once its date is known,
	// and reload when the approver changes that date.
	useEffect(() => {
		if (!isPrivileged) return;
		pendingItems.forEach((adj) => {
			const dateStr = formatApiDate(approvalDateValue(adj));
			if (!dateStr) return;
			const key = `${adj.id}:${dateStr}`;
			if (approveRoomsKey[adj.id] !== key) {
				loadApproveRooms(adj, dateStr, key);
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [items, approveDates, isPrivileged]);

	const handleSubmit = () => {
		if (!reason.trim()) {
			setError("Reason is required.");
			return;
		}
		if (!hearingId) {
			setError("Hearing is required.");
			return;
		}
		const formattedProposedDate = formatApiDate(proposedDate);
		if (isPrivileged && !formattedProposedDate) {
			setError("A proposed new hearing date is required to adjourn.");
			return;
		}
		// A direct adjournment re-books the hearing, so at least one free slot in a
		// room on the new date must be chosen.
		if (isPrivileged && selectedSlots.length === 0) {
			setError("Please pick a room and time slot for the new hearing date.");
			return;
		}
		setSubmitting(true);
		setError("");
		setNotice("");
		const payload = { reason: reason.trim(), is_adjourn: !isReschedule };
		if (formattedProposedDate) payload.proposed_date = formattedProposedDate;
		if (hearingId) payload.hearing = hearingId;
		if (isPrivileged && selectedSlots.length > 0) {
			payload.slot_ids = selectedSlots.map((slot) => slot.slot_id);
		}
		createAdjournment(disputeId, payload)
			.then(() => {
				setReason("");
				setProposedDate(null);
				setHearingId("");
				setSelectedSlots([]);
				setRooms([]);
				// A privileged user's adjournment is applied straight away, so it
				// never shows up in the pending list below — confirm it explicitly.
				setNotice(
					isPrivileged
						? (isReschedule
							? "Hearing rescheduled — the date and room have been updated."
							: "Case adjourned — the hearing date and room have been updated.")
						: (isReschedule
							? "Reschedule request submitted."
							: "Adjournment request submitted.")
				);
				fetchItems();
			})
			.catch((err) => {
				setError(err?.response?.data?.error || "Failed to submit request.");
			})
			.finally(() => setSubmitting(false));
	};

	const handleAction = (adj, action) => {
		setError("");
		setNotice("");
		const payload = { action };
		if (action === "rejected") {
			payload.action_note = rejectNotes[adj.id] || "";
		}
		if (action === "approved") {
			payload.action_note = rejectNotes[adj.id] || "";
			// Approver may set/override the new hearing date.
			const d = formatApiDate(approvalDateValue(adj));
			if (!d) {
				setError("A new hearing date is required to approve this request.");
				return;
			}
			payload.proposed_date = d;
			// Approval re-books the hearing onto free slot(s) on the new date.
			const slots = approveSlots[adj.id] || [];
			if (slots.length === 0) {
				setError("Please pick a room and time slot before approving.");
				return;
			}
			payload.slot_ids = slots.map((slot) => slot.slot_id);
		}
		setActioningId(adj.id);
		actionAdjournment(disputeId, adj.id, payload)
			.then(() => fetchItems())
			.catch((err) => {
				setError(err?.response?.data?.error || "Failed to update request.");
			})
			.finally(() => setActioningId(null));
	};

	return (
		<Paper className={classes.card}>
			<Box display="flex" justifyContent="space-between" alignItems="center">
				<Typography style={{ fontWeight: 600, fontSize: "1.2rem", color: "black" }}>
					{moduleTitle}
				</Typography>
				{onOpenPrevious && (
					<Typography
						onClick={onOpenPrevious}
						style={{
							cursor: "pointer",
							color: TEXT.info,
							fontSize: "0.85rem",
							fontWeight: 600,
							textDecoration: "underline",
						}}
					>
						Previous Requests
					</Typography>
				)}
			</Box>

			{/* Submit request form */}
			<Box style={{ marginTop: "1rem" }}>
				<TextField
					required
					select
					label="Hearing"
					value={hearingId}
					onChange={(e) => setHearingId(e.target.value)}
					fullWidth
					variant="outlined"
					size="small"
					helperText={
						hearings.length === 0 ? "No scheduled hearings found for this case." : " "
					}
				>
					<MenuItem value="">
						<em>Not specified</em>
					</MenuItem>
					{hearings.map((h) => (
						<MenuItem key={h.id} value={h.id}>
							{h.label}
						</MenuItem>
					))}
				</TextField>
				<Box height="0.25rem" />
				<TextField
					required
					label="Reason"
					value={reason}
					onChange={(e) => setReason(e.target.value)}
					multiline
					rows={1}
					rowsMax={6}
					fullWidth
					variant="outlined"
					size="small"
				/>
				<Box height="0.75rem" />
				<Box display="flex" style={{ gap: "0.75rem", alignItems: "stretch", flexWrap: "wrap" }}>
					<DatePickerACCB
						label={isPrivileged ? "New hearing date" : "Proposed date"}
						value={proposedDate}
						onChange={(newDate) => setProposedDate(newDate)}
						required={isPrivileged}
						fullWidth={false}
						minDate={new Date()}
					/>
					<Button
						variant="contained"
						color="primary"
						disabled={submitting}
						onClick={handleSubmit}
						style={{ textTransform: "none" }}
						size="small"
					>
						{submitting
							? "Submitting…"
							: isPrivileged
								? submitLabel
								: "Submit Request"}
					</Button>
				</Box>

				{/* Room + slot picker for a direct (privileged) adjournment. */}
				{isPrivileged && proposedDate && (
					<Box style={{ marginTop: "1rem" }}>
						<Typography
							style={{ fontWeight: 600, fontSize: "0.9rem", color: "black", marginBottom: "0.5rem" }}
						>
							Choose a room &amp; slot(s) for the new date
						</Typography>
						{roomsLoading ? (
							<Box display="flex" alignItems="center" style={{ gap: "0.5rem" }}>
								<CircularProgress size={16} />
								<Typography style={{ fontSize: "0.85rem", color: TEXT.muted }}>
									Loading available rooms…
								</Typography>
							</Box>
						) : roomsError ? (
							<Typography style={{ fontSize: "0.85rem", color: TEXT.error }}>
								{roomsError}
							</Typography>
						) : rooms.length === 0 ? (
							<Typography style={{ fontSize: "0.85rem", color: TEXT.muted }}>
								No hearing rooms available for this date.
							</Typography>
						) : (
							<>
								<RoomSlotPicker
									rooms={rooms}
									selectedSlots={selectedSlots}
									onChange={setSelectedSlots}
								/>
								{selectedSlots.length > 0 && (
									<Typography style={{ fontSize: "0.85rem", color: TEXT.success }}>
										Selected ({selectedSlots.length} slot{selectedSlots.length === 1 ? "" : "s"}): {slotSummary(selectedSlots)}
									</Typography>
								)}
							</>
						)}
					</Box>
				)}
			</Box>

			{error && (
				<Typography style={{ color: TEXT.error, fontSize: "0.85rem", marginTop: "0.75rem" }}>
					{error}
				</Typography>
			)}
			{notice && !error && (
				<Typography style={{ color: TEXT.success, fontSize: "0.85rem", marginTop: "0.75rem" }}>
					{notice}
				</Typography>
			)}

			{showPendingRequests && (
				<>
					<Typography
						style={{
							fontWeight: 600,
							fontSize: "0.95rem",
							color: "black",
							marginTop: "1.5rem",
							marginBottom: "0.75rem",
						}}
					>
						Pending Requests
					</Typography>

					{loading ? (
						<Box display="flex" alignItems="center" style={{ gap: "0.5rem" }}>
							<CircularProgress size={16} />
							<Typography style={{ fontSize: "0.85rem", color: TEXT.muted }}>
								Loading adjournments…
							</Typography>
						</Box>
					) : pendingItems.length === 0 ? (
						<Typography style={{ fontSize: "0.85rem", color: TEXT.muted }}>
							No pending adjournment requests.
						</Typography>
					) : (
						pendingItems.map((adj) => {
							const st = statusStyle(adj.status);
							const requester = requesterInfo(adj, partyLabels);
							const aRooms = approveRooms[adj.id] || [];
							const aSlots = approveSlots[adj.id] || [];
							const aDate = formatApiDate(approvalDateValue(adj));
							return (
								<Box
									key={adj.id}
									style={{
										border: "1px solid #E5E5E5",
										borderRadius: "6px",
										padding: "0.9rem 1rem",
										marginBottom: "0.75rem",
									}}
								>
									<Box display="flex" justifyContent="space-between" alignItems="center">
										<Chip
											label={st.label}
											size="small"
											style={{ backgroundColor: st.bg, color: st.color, fontWeight: 600 }}
										/>
										<Typography style={{ fontSize: "0.75rem", color: TEXT.muted }}>
											{safeDateFormat(adj.created_at)}
										</Typography>
									</Box>
									<Box height="0.5rem" />
									<RecordField
										label="Requested by"
										value={requester.role ? `${requester.role} — ${requester.name}` : requester.name}
									/>
									<RecordField label="Hearing" value={adj.hearing_label} />
									<RecordField label="Next hearing date" value={nextHearingText(adj)} />
									<RecordField label="Reason" value={previewReason(adj.reason)} />

									{/* Approve / Reject — only privileged users */}
									{isPrivileged && (
										<Box style={{ marginTop: "0.75rem" }}>
											<Box display="flex" style={{ gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
												<DatePickerACCB
													label="Set next hearing date"
													value={approvalDateValue(adj)}
													onChange={(newDate) => {
														setApproveDates((prev) => ({ ...prev, [adj.id]: newDate }));
													}}
													fullWidth={false}
												/>
												<TextField
													label="Comments"
													placeholder="Type..."
													value={rejectNotes[adj.id] || ""}
													onChange={(e) => {
														const val = e.target.value;
														setRejectNotes((prev) => ({ ...prev, [adj.id]: val }));
													}}
													variant="outlined"
													size="small"
												/>
											</Box>

											{/* Room + slot picker for approving (re-book on the new date). */}
											{aDate && (
												<Box style={{ marginTop: "0.75rem" }}>
													<Typography
														style={{ fontSize: "0.85rem", fontWeight: 600, color: "black", marginBottom: "0.5rem" }}
													>
														Choose a room &amp; slot(s) to approve
													</Typography>
													{aRooms.length === 0 ? (
														<Typography style={{ fontSize: "0.8rem", color: TEXT.muted }}>
															No rooms available for {safeDateFormat(aDate)} — pick another date.
														</Typography>
													) : (
														<>
															<RoomSlotPicker
																rooms={aRooms}
																selectedSlots={aSlots}
																onChange={(slots) =>
																	setApproveSlots((prev) => ({ ...prev, [adj.id]: slots }))
																}
															/>
															{aSlots.length > 0 && (
																<Typography style={{ fontSize: "0.8rem", color: TEXT.success }}>
																	Selected ({aSlots.length} slot{aSlots.length === 1 ? "" : "s"}): {slotSummary(aSlots)}
																</Typography>
															)}
														</>
													)}
												</Box>
											)}

											<Box height="0.5rem" />
											<Box display="flex" style={{ gap: "0.5rem" }}>
												<Button
													variant="contained"
													color="primary"
													size="small"
													disabled={actioningId === adj.id}
													onClick={() => handleAction(adj, "approved")}
													style={{ textTransform: "none" }}
												>
													Approve
												</Button>
												<Button
													variant="outlined"
													size="small"
													disabled={actioningId === adj.id}
													onClick={() => handleAction(adj, "rejected")}
													style={{ textTransform: "none", color: TEXT.error, borderColor: "#EB5757" }}
												>
													Reject
												</Button>
											</Box>
										</Box>
									)}
								</Box>
							);
						})
					)}
				</>
			)}
		</Paper>
	);
}
